import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";

export const useUserReservations = () => {
  const { data: profile } = useProfileQuery();

  return useQuery({
    queryKey: ['user-reservations', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      // First, get the reservations
      const { data: reservations, error: reservationsError } = await supabase
        .from('reservations')
        .select('*')
        .eq('user_id', profile.id)
        .order('reservation_date', { ascending: false });

      if (reservationsError) throw reservationsError;
      if (!reservations || reservations.length === 0) return [];

      // Get unique item IDs
      const itemIds = [...new Set(reservations.map(r => r.item_id))];

      // Get reservation items with categories
      const { data: items, error: itemsError } = await supabase
        .from('reservation_items')
        .select('id, name, description, image_url, category_id')
        .in('id', itemIds);

      if (itemsError) throw itemsError;

      // Get categories for the items
      const categoryIds = items ? [...new Set(items.map(item => item.category_id))] : [];
      const { data: categories, error: categoriesError } = await supabase
        .from('reservation_categories')
        .select('id, name, gym_id')
        .in('id', categoryIds);

      if (categoriesError) throw categoriesError;

      // Get gym information for reservation messages
      const gymIds = categories ? [...new Set(categories.map(cat => cat.gym_id))] : [];
      const { data: gyms, error: gymsError } = await supabase
        .from('gyms')
        .select('id, reservation_message')
        .in('id', gymIds);

      if (gymsError) throw gymsError;

      // Combine the data
      const enrichedReservations = reservations.map(reservation => {
        const item = items?.find(item => item.id === reservation.item_id);
        const category = item ? categories?.find(cat => cat.id === item.category_id) : null;
        const gym = category ? gyms?.find(g => g.id === category.gym_id) : null;
        
        return {
          ...reservation,
          reservation_items: {
            name: item?.name || '',
            description: item?.description || '',
            image_url: item?.image_url || '',
            reservation_categories: {
              name: category?.name || ''
            }
          },
          gym_message: gym?.reservation_message || null
        };
      });

      return enrichedReservations;
    },
    enabled: !!profile?.id,
  });
};